import { useState, useRef, useEffect, useMemo } from 'react';
import { Moon, Sun, Monitor, Droplets, ChevronDown } from 'lucide-react';
import { useTheme, type Theme } from '@/hooks/useTheme';
import { GlassButton } from '@/components/ui/glass-button';

const themeOptions: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'cyan', label: 'Cyan', icon: Droplets },
  { value: 'system', label: 'System', icon: Monitor },
];

export function ThemeToggle() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const currentOption = themeOptions.find((opt) => opt.value === theme) ?? themeOptions[0];
  const CurrentIcon = currentOption!.icon;

  // Get the icon for the resolved theme (what's actually displayed)
  const ResolvedIcon = useMemo(() => {
    if (theme === 'system') {
      return resolvedTheme === 'dark' ? Moon : Sun;
    }
    return CurrentIcon;
  }, [theme, resolvedTheme, CurrentIcon]);

  return (
    <div className="relative" ref={dropdownRef}>
      <GlassButton
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select theme"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className="flex items-center gap-1"
      >
        <ResolvedIcon size={20} />
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </GlassButton>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-36 rounded-md border border-border bg-popover p-1 shadow-lg z-50"
          role="listbox"
          aria-label="Theme options"
        >
          {themeOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = theme === option.value;
            return (
              <button
                key={option.value}
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  setTheme(option.value);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors ${
                  isSelected
                    ? 'bg-accent text-accent-foreground'
                    : 'text-popover-foreground hover:bg-muted'
                }`}
              >
                <Icon size={16} />
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
